import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, BarChart2, Users, Clock, Receipt,
  TrendingUp, List, DollarSign,
  Download, Printer, RefreshCw, Search, ChevronUp, ChevronDown
} from 'lucide-react';
import apiClient from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Report type definitions
// ─────────────────────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { key: 'ledger_details',   label: 'Ledger Details',   icon: FileText   },
  { key: 'ledger_summary',   label: 'Ledger Summary',   icon: BarChart2  },
  { key: 'aging',            label: 'Aging Report',     icon: Clock      },
  { key: 'customer_list',    label: 'Customer List',    icon: Users      },
  { key: 'invoice_details',  label: 'Invoice Details',  icon: Receipt    },
  { key: 'invoice_summary',  label: 'Invoice Summary',  icon: TrendingUp },
  { key: 'receipt_details',  label: 'Receipt Details',  icon: DollarSign },
  { key: 'receipt_summary',  label: 'Receipt Summary',  icon: List       },
];

// ─────────────────────────────────────────────────────────────────────────────
// Column definitions per report
// ─────────────────────────────────────────────────────────────────────────────
const COLUMNS = {
  ledger_details: [
    { key: 'CustomerCode',    label: 'Code',        width: 90  },
    { key: 'CustomerName',    label: 'Customer',    width: 170 },
    { key: 'TransactionDate', label: 'Date',        width: 95,  fmt: 'date' },
    { key: 'ReferenceNo',     label: 'Reference',   width: 120 },
    { key: 'TransactionType', label: 'Type',        width: 75,  fmt: 'txntype' },
    { key: 'DebitAmount',     label: 'Debit',       width: 110, align: 'right', fmt: 'num2' },
    { key: 'CreditAmount',    label: 'Credit',      width: 110, align: 'right', fmt: 'num2green' },
    { key: 'RunningBalance',  label: 'Balance',     width: 120, align: 'right', fmt: 'balance' },
    { key: 'AgingCategory',   label: 'Aging',       width: 95,  fmt: 'aging' },
    { key: 'DaysOverdue',     label: 'Days OD',     width: 70,  align: 'right', fmt: 'overdue' },
    { key: 'TransactionStatus',label:'Status',      width: 90,  fmt: 'invstatus' },
    { key: 'Description',     label: 'Description', width: 180 },
  ],
  ledger_summary: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 200 },
    { key: 'TotalInvoices',   label: 'Invoices',     width: 80,  align: 'right' },
    { key: 'DebitAmount',     label: 'Total Invoiced',width: 130, align: 'right', fmt: 'num2' },
    { key: 'CreditAmount',    label: 'Total Paid',   width: 130, align: 'right', fmt: 'num2green' },
    { key: 'RunningBalance',  label: 'Balance',      width: 130, align: 'right', fmt: 'balance' },
    { key: 'FirstTransaction',label: 'First Txn',   width: 100, fmt: 'date' },
    { key: 'LastTransaction', label: 'Last Txn',    width: 100, fmt: 'date' },
  ],
  aging: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 160 },
    { key: 'InvoiceNo',       label: 'Invoice No',   width: 120 },
    { key: 'InvoiceDate',     label: 'Invoice Date', width: 100, fmt: 'date' },
    { key: 'DueDate',         label: 'Due Date',     width: 100, fmt: 'date' },
    { key: 'InvoiceAmount',   label: 'Amount',       width: 110, align: 'right', fmt: 'num2' },
    { key: 'PaidAmount',      label: 'Paid',         width: 100, align: 'right', fmt: 'num2green' },
    { key: 'OutstandingAmount',label:'Outstanding',  width: 120, align: 'right', fmt: 'balance' },
    { key: 'DaysOverdue',     label: 'Days OD',      width: 70,  align: 'right', fmt: 'overdue' },
    { key: 'AgingCategory',   label: 'Category',     width: 100, fmt: 'aging' },
    { key: 'CurrentAmt',      label: 'Current',      width: 100, align: 'right', fmt: 'num2' },
    { key: 'Days1to30',       label: '1-30 Days',    width: 100, align: 'right', fmt: 'num2' },
    { key: 'Days31to60',      label: '31-60 Days',   width: 100, align: 'right', fmt: 'num2' },
    { key: 'Days61to90',      label: '61-90 Days',   width: 100, align: 'right', fmt: 'num2' },
    { key: 'Over90Days',      label: 'Over 90',      width: 100, align: 'right', fmt: 'num2red' },
    { key: 'Status',          label: 'Status',       width: 90,  fmt: 'invstatus' },
  ],
  customer_list: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer Name',width: 180 },
    { key: 'CustomerType',    label: 'Type',         width: 80  },
    { key: 'Phone',           label: 'Phone',        width: 120 },
    { key: 'Email',           label: 'Email',        width: 170 },
    { key: 'CreditLimit',     label: 'Credit Limit', width: 110, align: 'right', fmt: 'num2' },
    { key: 'TotalInvoiced',   label: 'Invoiced',     width: 120, align: 'right', fmt: 'num2' },
    { key: 'TotalPaid',       label: 'Paid',         width: 110, align: 'right', fmt: 'num2green' },
    { key: 'OutstandingBalance',label:'Outstanding', width: 120, align: 'right', fmt: 'balance' },
    { key: 'TotalInvoices',   label: '# Invoices',   width: 80,  align: 'right' },
    { key: 'LastInvoiceDate', label: 'Last Invoice', width: 110, fmt: 'date' },
    { key: 'Address',         label: 'Address',      width: 200 },
  ],
  invoice_details: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 160 },
    { key: 'InvoiceNo',       label: 'Invoice No',   width: 120 },
    { key: 'InvoiceDate',     label: 'Date',         width: 95,  fmt: 'date' },
    { key: 'DueDate',         label: 'Due Date',     width: 95,  fmt: 'date' },
    { key: 'JobReference',    label: 'Job Ref',      width: 110 },
    { key: 'SubTotal',        label: 'Sub Total',    width: 110, align: 'right', fmt: 'num2' },
    { key: 'TaxAmount',       label: 'Tax',          width: 90,  align: 'right', fmt: 'num2' },
    { key: 'DiscountAmount',  label: 'Discount',     width: 90,  align: 'right', fmt: 'num2' },
    { key: 'InvoiceAmount',   label: 'Total',        width: 120, align: 'right', fmt: 'num2' },
    { key: 'PaidAmount',      label: 'Paid',         width: 110, align: 'right', fmt: 'num2green' },
    { key: 'OutstandingAmount',label:'Outstanding',  width: 120, align: 'right', fmt: 'balance' },
    { key: 'Status',          label: 'Status',       width: 95,  fmt: 'invstatus' },
    { key: 'AgingCategory',   label: 'Aging',        width: 95,  fmt: 'aging' },
    { key: 'ConsignmentRefs', label: 'Consignments', width: 160 },
    { key: 'LineItemCount',   label: 'Lines',        width: 55,  align: 'right' },
  ],
  invoice_summary: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 170 },
    { key: 'TotalInvoices',   label: 'Invoices',     width: 80,  align: 'right' },
    { key: 'TotalSubTotal',   label: 'Sub Total',    width: 120, align: 'right', fmt: 'num2' },
    { key: 'TotalTax',        label: 'Tax',          width: 100, align: 'right', fmt: 'num2' },
    { key: 'TotalDiscount',   label: 'Discount',     width: 100, align: 'right', fmt: 'num2' },
    { key: 'TotalInvoiced',   label: 'Total Invoiced',width:130, align: 'right', fmt: 'num2' },
    { key: 'TotalPaid',       label: 'Total Paid',   width: 120, align: 'right', fmt: 'num2green' },
    { key: 'TotalOutstanding',label: 'Outstanding',  width: 120, align: 'right', fmt: 'balance' },
    { key: 'AvgInvoiceAmount',label: 'Avg Invoice',  width: 110, align: 'right', fmt: 'num2' },
    { key: 'PaidCount',       label: 'Paid #',       width: 65,  align: 'right' },
    { key: 'UnpaidCount',     label: 'Unpaid #',     width: 70,  align: 'right' },
    { key: 'FirstInvoiceDate',label: 'First',        width: 95,  fmt: 'date' },
    { key: 'LastInvoiceDate', label: 'Last',         width: 95,  fmt: 'date' },
  ],
  receipt_details: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 170 },
    { key: 'ReceiptNo',       label: 'Voucher No',   width: 120 },
    { key: 'ReceiptDate',     label: 'Date',         width: 95,  fmt: 'date' },
    { key: 'ReceiptAmount',   label: 'Amount',       width: 120, align: 'right', fmt: 'num2green' },
    { key: 'PaymentAccount',  label: 'Account',      width: 160 },
    { key: 'PaymentCategory', label: 'Category',     width: 120 },
    { key: 'Status',          label: 'Status',       width: 90  },
    { key: 'Description',     label: 'Description',  width: 200 },
  ],
  receipt_summary: [
    { key: 'CustomerCode',    label: 'Code',         width: 90  },
    { key: 'CustomerName',    label: 'Customer',     width: 200 },
    { key: 'TotalReceipts',   label: 'Receipts',     width: 90,  align: 'right' },
    { key: 'TotalReceived',   label: 'Total Received',width:140, align: 'right', fmt: 'num2green' },
    { key: 'AvgReceiptAmount',label: 'Avg Amount',   width: 120, align: 'right', fmt: 'num2' },
    { key: 'FirstReceiptDate',label: 'First',        width: 100, fmt: 'date' },
    { key: 'LastReceiptDate', label: 'Last',         width: 100, fmt: 'date' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────
const formatDate = v => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmt2    = v => v != null && v !== '' ? parseFloat(v).toFixed(2) : '';
const AGING_COLORS = {
  'Current':    { bg: '#f0fdf4', color: '#15803d' },
  '1-30 Days':  { bg: '#eff6ff', color: '#1d4ed8' },
  '31-60 Days': { bg: '#fefce8', color: '#a16207' },
  '61-90 Days': { bg: '#fff7ed', color: '#c2410c' },
  'Over 90 Days':{ bg:'#fef2f2', color: '#dc2626' },
};
const INV_STATUS_COLORS = {
  'Paid':           '#15803d',
  'Partially Paid': '#d97706',
  'Overdue':        '#dc2626',
  'Sent':           '#1d4ed8',
  'Draft':          '#6b7280',
  'Cancelled':      '#9ca3af',
};

const CellValue = ({ col, value }) => {
  const v = value ?? '';
  switch (col.fmt) {
    case 'date':
      return <span>{formatDate(v)}</span>;
    case 'num2':
      return <span style={{ fontFamily: '"Courier New",monospace' }}>{fmt2(v)}</span>;
    case 'num2green':
      return <span style={{ fontFamily: '"Courier New",monospace', color: parseFloat(v) > 0 ? '#15803d' : undefined }}>{fmt2(v)}</span>;
    case 'num2red':
      return <span style={{ fontFamily: '"Courier New",monospace', color: parseFloat(v) > 0 ? '#dc2626' : undefined, fontWeight: parseFloat(v) > 0 ? '700' : undefined }}>{fmt2(v)}</span>;
    case 'balance': {
      const n = parseFloat(v) || 0;
      return <span style={{ fontFamily: '"Courier New",monospace', color: n > 0 ? '#dc2626' : n < 0 ? '#15803d' : undefined, fontWeight: '700' }}>{fmt2(v)}</span>;
    }
    case 'txntype': {
      const isReceipt = String(v).toLowerCase() === 'receipt';
      return <span style={{ fontWeight: '600', color: isReceipt ? '#15803d' : '#1d4ed8' }}>{v}</span>;
    }
    case 'aging': {
      const style = AGING_COLORS[v] || {};
      return <span style={{ padding: '2px 7px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '600', background: style.bg, color: style.color }}>{v}</span>;
    }
    case 'overdue': {
      const n = parseInt(v) || 0;
      return <span style={{ fontFamily: '"Courier New",monospace', fontWeight: n > 0 ? '700' : undefined, color: n > 90 ? '#dc2626' : n > 60 ? '#ea580c' : n > 30 ? '#d97706' : n > 0 ? '#ca8a04' : undefined }}>{v}</span>;
    }
    case 'invstatus': {
      const c = INV_STATUS_COLORS[v] || '#374151';
      return <span style={{ fontWeight: '600', color: c }}>{v}</span>;
    }
    default:
      return <span>{String(v)}</span>;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const CustomerReports = () => {
  const today      = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  const [reportType, setReportType] = useState('ledger_details');
  const [startDate,  setStartDate]  = useState(monthStart);
  const [endDate,    setEndDate]    = useState(today);
  const [customerID, setCustomerID] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [customers,  setCustomers]  = useState([]);
  const [rows,       setRows]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [hasLoaded,  setHasLoaded]  = useState(false);

  const [sortKey,    setSortKey]    = useState('');
  const [sortDir,    setSortDir]    = useState('asc');

  // Load customer dropdown on mount
  useEffect(() => {
    apiClient.get('/CustomerReports.php?action=dropdowns')
      .then(res => {
        if (res.data.success) setCustomers(res.data.data.customers || []);
      })
      .catch(err => console.error('Dropdown error:', err));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setHasLoaded(false);
    setSortKey('');
    const params = new URLSearchParams({
      report_type: reportType,
      start_date:  startDate,
      end_date:    endDate,
      customer_id: customerID,
    });
    try {
      const res = await apiClient.get(`/CustomerReports.php?${params}`);
      if (res.data.success) {
        setRows(res.data.data.rows || []);
        setSummary(res.data.data.summary || {});
        setHasLoaded(true);
      } else {
        setError(res.data.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error loading report');
    } finally {
      setLoading(false);
    }
  }, [reportType, startDate, endDate, customerID]);

  // Client-side search + sort
  const displayRows = (() => {
    let r = rows;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter(row => Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q)));
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  })();

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const exportCSV = () => {
    const cols = COLUMNS[reportType] || [];
    const header = cols.map(c => c.label).join(',');
    const body   = displayRows.map(row =>
      cols.map(c => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = `${reportType}_${startDate}_${endDate}.csv`;
    a.click();
  };

  const handlePrint = () => {
    const cols = COLUMNS[reportType] || [];
    const rt   = REPORT_TYPES.find(r => r.key === reportType);
    const win  = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${rt?.label}</title>
      <style>
        body  { font-family: Arial, sans-serif; font-size: 11px; color: #1f2937; }
        h2    { margin: 0 0 4px; font-size: 15px; }
        p.sub { margin: 0 0 10px; color:#555; font-size:10px; }
        table { border-collapse: collapse; width: 100%; }
        th    { background: #667eea; color: #fff; padding: 5px 7px; font-size: 10px; text-align: left; }
        td    { padding: 4px 7px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #f9fafb; }
        @media print { @page { margin: 10mm; } }
      </style></head><body>
      <h2>Customer Reports — ${rt?.label}</h2>
      <p class="sub">Period: ${startDate} to ${endDate} &nbsp;|&nbsp; Records: ${displayRows.length}</p>
      <table>
        <thead><tr>${cols.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
        <tbody>
          ${displayRows.map(row =>
            `<tr>${cols.map(c => {
              let v = row[c.key] ?? '';
              if (c.fmt === 'date') v = formatDate(v);
              else if (c.fmt && c.fmt.startsWith('num')) v = fmt2(v);
              const align = c.align === 'right' ? ' style="text-align:right"' : '';
              return `<td${align}>${v}</td>`;
            }).join('')}</tr>`
          ).join('')}
        </tbody>
      </table>
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`);
    win.document.close();
  };

  const cols = COLUMNS[reportType] || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f9fafb' }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px 28px',
        display: 'flex', alignItems: 'center', gap: '20px',
        borderBottom: '4px solid #5a67d8', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          width: '52px', height: '52px',
          background: 'rgba(255,255,255,0.25)', borderRadius: '14px',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Users size={26} color="#ffffff" />
        </div>
        <div>
          <div style={{ color: '#fff', fontSize: '1.7rem', fontWeight: '800', textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>
            Customer Reports
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', marginTop: '2px' }}>
            {REPORT_TYPES.find(r => r.key === reportType)?.label}
            {hasLoaded && ` · ${displayRows.length} records`}
          </div>
        </div>
      </div>

      {/* ── Report Tabs ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', overflowX: 'auto', gap: '4px',
        padding: '12px 16px', background: '#f3f4f6',
        borderBottom: '1px solid #e5e7eb', flexShrink: 0
      }}>
        {REPORT_TYPES.map(rt => {
          const active = rt.key === reportType;
          const Icon   = rt.icon;
          return (
            <button key={rt.key}
              onClick={() => { setReportType(rt.key); setHasLoaded(false); setSortKey(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px', borderRadius: '8px', border: 'none',
                cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem',
                fontWeight: active ? '700' : '500',
                background: active ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'white',
                color: active ? '#fff' : '#374151',
                boxShadow: active ? '0 2px 8px rgba(102,126,234,0.4)' : '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all .15s'
              }}
            >
              <Icon size={13} />{rt.label}
            </button>
          );
        })}
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={lbl}>From Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>To Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Customer</label>
            <select value={customerID} onChange={e => setCustomerID(e.target.value)} style={inp}>
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.customerid} value={c.customerid}>
                  {c.customercode} – {c.customername}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>&nbsp;</label>
            <button onClick={loadReport} disabled={loading} style={{
              width: '100%', padding: '9px 14px', borderRadius: '7px',
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg,#667eea,#764ba2)',
              color: '#fff', fontWeight: '700', fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}>
              {loading ? <><div style={spinnerStyle} />Loading…</> : <><RefreshCw size={14} />Load Report</>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Summary bar + toolbar ───────────────────────────────────────────── */}
      {hasLoaded && (
        <div style={{
          padding: '10px 20px', background: '#f8fafc',
          borderBottom: '1px solid #e5e7eb', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'
        }}>
          <StatBadge label="Records" value={displayRows.length} color="#667eea" />
          {summary.total_debit  > 0 && <StatBadge label="Total Debit"  value={parseFloat(summary.total_debit).toFixed(2)}  color="#dc2626" />}
          {summary.total_credit > 0 && <StatBadge label="Total Credit" value={parseFloat(summary.total_credit).toFixed(2)} color="#15803d" />}
          {summary.total_balance != null && <StatBadge label="Net Balance" value={parseFloat(summary.total_balance).toFixed(2)} color={parseFloat(summary.total_balance) >= 0 ? '#dc2626' : '#15803d'} />}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '7px', background: '#fff' }}>
              <Search size={14} color="#9ca3af" />
              <input type="text" placeholder="Search…" value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ border: 'none', outline: 'none', width: '140px', fontSize: '0.85rem' }} />
            </div>
            <button onClick={exportCSV} style={actionBtn}>
              <Download size={15} /> CSV
            </button>
            <button onClick={handlePrint} style={actionBtn}>
              <Printer size={15} /> Print
            </button>
          </div>
        </div>
      )}

      {/* ── Error ────────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ margin: '12px 20px', padding: '10px 14px', borderRadius: '8px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: '0.9rem', flexShrink: 0 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!hasLoaded && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
            <Users size={48} style={{ margin: '0 auto 12px', opacity: .4 }} />
            <p style={{ margin: 0, fontSize: '1rem' }}>
              Select filters and click <strong>Load Report</strong>
            </p>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#667eea' }}>
            <div style={{ ...spinnerStyle, margin: '0 auto 12px', width: '32px', height: '32px', borderWidth: '3px' }} />
            <p style={{ margin: 0 }}>Loading report…</p>
          </div>
        )}

        {hasLoaded && !loading && (
          displayRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
              <FileText size={40} style={{ margin: '0 auto 10px', opacity: .4 }} />
              <p style={{ margin: 0 }}>No data found for the selected filters.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: '800px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0, zIndex: 2 }}>
                  {cols.map(c => (
                    <th key={c.key} onClick={() => handleSort(c.key)}
                      style={{
                        padding: '9px 10px', textAlign: c.align || 'left',
                        fontWeight: '700', fontSize: '0.78rem', color: '#374151',
                        cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                        borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb',
                        minWidth: c.width
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        {c.label}
                        {sortKey === c.key
                          ? sortDir === 'asc' ? <ChevronUp size={12} color="#667eea" /> : <ChevronDown size={12} color="#667eea" />
                          : <ChevronUp size={12} color="#d1d5db" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, i) => (
                  <tr key={i}
                    style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9fafb'}
                  >
                    {cols.map(c => (
                      <td key={c.key} style={{
                        padding: '7px 10px',
                        borderBottom: '1px solid #e5e7eb',
                        borderRight: '1px solid #f1f5f9',
                        textAlign: c.align || 'left',
                        whiteSpace: c.width > 140 ? 'normal' : 'nowrap',
                        fontFamily: c.align === 'right' ? '"Courier New",monospace' : 'inherit',
                      }}>
                        <CellValue col={c} value={row[c.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {/* Totals footer */}
              {displayRows.length > 1 && (
                <tfoot>
                  <tr style={{ background: '#f1f5f9', fontWeight: '700' }}>
                    {cols.map((c, ci) => {
                      if (ci === 0) return (
                        <td key={c.key} style={tdFoot}>TOTAL ({displayRows.length})</td>
                      );
                      const numFmts = ['num2','num2green','num2red','num2','balance'];
                      if (numFmts.includes(c.fmt) || c.align === 'right') {
                        const isCount = !c.fmt || c.fmt === '';
                        if (isCount && c.align === 'right') {
                          const sum = displayRows.reduce((s, r) => s + (parseInt(r[c.key]) || 0), 0);
                          return <td key={c.key} style={{ ...tdFoot, textAlign: 'right', fontFamily: '"Courier New",monospace' }}>{sum}</td>;
                        } 
                        if ((c.fmt && c.fmt.startsWith('num')) || c.fmt === 'balance')  {
                          const sum = displayRows.reduce((s, r) => s + (parseFloat(r[c.key]) || 0), 0);
                          return <td key={c.key} style={{ ...tdFoot, textAlign: 'right', fontFamily: '"Courier New",monospace' }}>{fmt2(sum)}</td>;
                        }
                      }
                      return <td key={c.key} style={tdFoot} />;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#374151', marginBottom: '4px' };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' };
const spinnerStyle = { width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block' };
const actionBtn = { display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: '7px', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' };
const tdFoot = { padding: '8px 10px', borderTop: '2px solid #d1d5db', fontSize: '0.8rem', color: '#1f2937' };

const StatBadge = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
    <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>{label}</span>
    <span style={{ fontSize: '1rem', fontWeight: '800', color, fontFamily: '"Courier New",monospace' }}>{value}</span>
  </div>
);

export default CustomerReports;